/*
 * Failures to load JavaScript chunks after a deploy, reported to Sentry.
 */
import {
  attemptStaleModuleReload,
  isStaleModuleError,
  takePendingChunkReload,
} from '@components/router/stale-module-reload';
import { captureChunkLoadError, captureWarning } from 'client/lib/sentry';

/**
 * Handle a rejected dynamic import. Reloads the page when a chunk is missing
 * (usually because a deploy replaced it), otherwise reports the error.
 * Returns true when a reload has started.
 */
export function handleLazyLoadError(err: unknown, context: string): boolean {
  if (isStaleModuleError(err) && attemptStaleModuleReload(context, err)) {
    return true;
  }
  captureChunkLoadError(err, context);
  return false;
}

/**
 * Vite fires this when a dynamic import or one of its dependencies fails.
 * When a file is missing, schedule a reload. The import still rejects:
 * preventing the event would resolve it with undefined, and callers would
 * crash on the missing module before the reload. While the reload is
 * pending, Sentry drops these errors and handleLazyLoadError skips them.
 */
export function listenForPreloadErrors() {
  window.addEventListener('vite:preloadError', (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    if (isStaleModuleError(payload)) {
      attemptStaleModuleReload('preload', payload);
    }
  });
}

// A deploy can make every open tab reload once, so report a sample of the
// reloads that recovered rather than all of them.
const RECOVERED_RELOAD_SAMPLE_RATE = 0.1;

/**
 * Report a reload that happened because a JavaScript file failed to load, now
 * that the page has loaded and Sentry is running.
 */
export function reportPendingChunkReload() {
  const pending = takePendingChunkReload();
  if (!pending || Math.random() >= RECOVERED_RELOAD_SAMPLE_RATE) {
    return;
  }
  captureWarning('Reloaded the page after a JavaScript file failed to load', {
    tags: { chunk_load: pending.context },
    extra: { ...pending, sampleRate: RECOVERED_RELOAD_SAMPLE_RATE },
  });
}
